/*
 * File: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice\src\Content\Contact\AddressContact\AddressContact.tsx
 * Project: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice
 * Created Date: Friday July 7th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Tuesday, 19th September 2023 03:53:52
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 */

import { ContactSelect } from "Content/Contact/ContactSelect/ContactSelect";
import { useLang } from "Router/LangueProvider";
import { forwardRef, useImperativeHandle, useEffect, useRef, useState } from "react";
import { getAddresses } from "Services/Address/AddressService";
import CreatableSelect from 'react-select/creatable';
import Styles from "Content/Contact/AddressContact/AddressContact.module.css";
import { OptionType } from "Content/Contact/ContactSelect/OptionTypeInterface";
import { ActionMeta, InputActionMeta, SingleValue } from "react-select";
import depList from "Content/Contact/AddressContact/DepartmentListe";
import "./inputSelect.css";
import { Form, Col } from "react-bootstrap";
import { TextField } from "@mui/material";
import { AddressContactProps2 } from "./AddressContactProps.type";

const AddressContact = forwardRef((props: AddressContactProps2, ref) =>  {
  const {translator} = useLang();
  const [selectedAdress, setSelectedAdress] = useState<OptionType | null>(null);
  const [addressOptions, setAddressOptions] = useState<OptionType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [adressCity, setAdressCity] = useState<string>();
  const [postalCode, setPostalCode] = useState<string>();
  const [country, setCountry] = useState<string>();
  const [badPostalCode, setBadPostalCode] = useState(false);
  const [adressDepartment, setAdressDepartment] = useState<OptionType>({label: translator.decode("contact.stateSelect"), value: ""});
  const [departmentOptions, setDepartmentOptions] = useState<OptionType[]>(depList);
  const address = useRef("");
  const curChar = useRef(0);

  useImperativeHandle(ref, () => {
    if (props.onUpdate)
      props.onUpdate();
    return {
      address: address.current,
      city: adressCity,
      postalCode,
      state: adressDepartment.label,
      country: country
    };
  }, [adressCity, postalCode, selectedAdress, country]);

  const inputChanges = (input: string, action: InputActionMeta) => {
    const tmp = input.trim();
    if (tmp && tmp.length > 3 && tmp.length !== curChar.current) {
      curChar.current = input.length;
      setIsLoading(true);
      getAddresses(input).then((val: any) => {
        if (val && val.length > 0) {
          setAddressOptions(val);
          setIsLoading(false);
        } else {
          setAddressOptions([{label: input, value: input}]);
          setIsLoading(false);
        }
      }).catch((reason: any) => {
        setAddressOptions([{label: input, value: input}]);
        setIsLoading(false);
      });
    }
  };

  const filterOptions = (
    _: { label: string; value: string; data: any },
    __: string
    ) => {
      return true;
  };

  const handleCreate = (inputValue: string) => {
    setIsLoading(true);
    const newOption = {label: inputValue, value: inputValue};
    setAddressOptions((_) => [...addressOptions, newOption]);
    setIsLoading(false);
    setSelectedAdress(newOption);
  };

  useEffect(() => {
    setBadPostalCode(false);
    if (postalCode === undefined)
      return;
  }, [postalCode]);

  const handleSelection = (newValue: SingleValue<OptionType>, actionMeta: ActionMeta<OptionType>) => {
    setSelectedAdress(newValue != null ? {label: `${newValue.data.houseNumber ?? ""} ${newValue.data.street} - ${newValue.data.city}`, value: newValue.value} : {label: "", value: ""});
    address.current = `${newValue?.data?.houseNumber ? newValue?.data?.houseNumber.toString() + " " : ""}${newValue?.data?.street ? newValue?.data?.street.toString() : ""}`;
    setAdressCity(newValue != null && newValue.data ? newValue.data.city : "");
    setPostalCode(newValue?.data.postalCode);

    if (newValue !== null) {
      const dept = departmentOptions.find((x) => newValue.data.postalCode.startsWith(x.value)) as OptionType;
      if (dept)
        setAdressDepartment(dept);
      else if (newValue && newValue.data && newValue.data.state) {
        setDepartmentOptions([{label: newValue.data.state, value: newValue.data.state}]);
        setAdressDepartment({label: newValue.data.state, value: newValue.data.state});
      }
    } else {
      setDepartmentOptions(depList);
      setAdressDepartment({label: translator.decode("contact.stateSelect"), value: ""});
    }

    setCountry(newValue !== null && newValue.data ? newValue.data.country : "");

    if (props.onUpdate)
      props.onUpdate();
  };

  const muiStyle = {
    "& .MuiFormLabel-asterisk": {
      color: "red"
    }
  };

  return (
    <Form.Group className="d-flex flex-column gap-3">
      <h3 className="m-0">{translator.decode("dynamic.address")}</h3>
      <Col>
        <div className={`${Styles.addressBlock}`}>
          <CreatableSelect isLoading={isLoading} onBlur={() => props.onUpdate ? props.onUpdate() : null} onChange={handleSelection} onInputChange={inputChanges} filterOption={filterOptions}
                            className={`${Styles.input} ${Styles.addressSize} ${Styles.inputSelect}`} id="address" value={selectedAdress} isClearable
                            onCreateOption={handleCreate} options={addressOptions} isSearchable={true} placeholder={<div>{translator.decode("contact.addressSelect")} <span className={`${Styles.asterisk}`}>*</span></div>}
                            styles={{
                              singleValue: (provided: any, state: any) => ({
                                ...provided,
                                overflow: "initial",
                              }),
                              control: (provided: any, state: any) => ({
                                ...provided,
                                boxShadow: "none",
                                borderRadius: "5px",
                                marginBottom: "1rem",
                              }),
                              menu: (provided: any, state: any) => ({
                                ...provided,
                                border: "solid grey 1px",
                                zIndex: "100 !important",
                                backgroundColor: "white",
                              }),
                              option: (provided: any, state: any) => ({
                                ...provided,
                                backgroundColor: state.isFocused && "grey",
                                color: state.isFocused && "white",
                              })
                            }} />
        </div>
        <div className={`${Styles.lineBlock}`}>
          <div className={`${Styles.inputBlock}`}>
            <ContactSelect id="state" value={adressDepartment} onChange={(e: OptionType) => {setAdressDepartment(e);}} options={departmentOptions} />
          </div>
          <div className={`${Styles.inputBlock}`}>
            <TextField size="small" sx={muiStyle} required label={translator.decode("contact.postalCodeSelect")} className={`${Styles.input} ${Styles.inputSize} ${Styles.addressSize} ${badPostalCode ? Styles.wrongInput : null}`} key={postalCode} value={ postalCode } onChange={(e) => {setPostalCode(e.target.value); props.address.postalCode = e.target.value;}} />
            {
              badPostalCode ? <p className={Styles.mandatory}>{translator.decode("contact.wrong.postalCode")}</p> : null
            }
          </div>
          <div className={`${Styles.inputBlock}`}>
            <TextField required sx={{background: "white", ...muiStyle}} size="small" label={translator.decode("contact.city")} className={`${Styles.input} ${Styles.inputSize} ${Styles.addressSize}`} key={adressCity} value={adressCity} onChange={(e) => {setAdressCity(e.target.value); props.address.city = e.target.value;}}/>
          </div>
          <div className={`${Styles.inputBlock}`}>
            <TextField required size="small" sx={muiStyle} label={translator.decode("contact.country")} className={`${Styles.input} ${Styles.inputSize} ${Styles.addressSize}`} key={country} value={country} onChange={(e) => {setCountry(e.target.value)}} />
          </div>
        </div>
      </Col>
    </Form.Group>
  );
})

export default AddressContact;
